// Rules for on-demand trip offers and the Marketplace tile.
//
// Kept out of the components so they can be tested without pulling in the whole
// dashboard/map tree, and so both the Dashboard tile and the App route guard read the
// same rule instead of restating it.

// Dispatch types whose work is a fixed, dispatcher-assigned sequence rather than
// self-claimed live work.
export const FIXED_SEQUENCE_DISPATCH_TYPES = ['itinerary', 'shuttlebus-looped', 'shuttlebus-fixed'];

// Dispatch types where the driver may browse and claim marketplace work. Itinerary is
// included even though it is a fixed-sequence type: an itinerary driver plans their own
// day around the route they were given, and claiming an extra trip is a business
// decision the fleet enables per-fleet (driverSelfAssembly), not something the mode
// itself forbids. The shuttle types stay out — a looped/fixed shuttle has no room in
// its sequence for an ad-hoc trip.
const MARKETPLACE_DISPATCH_TYPES = ['live', 'itinerary'];

/**
 * Marketplace lets a driver browse unassigned work and claim or bid for it. It is
 * supported in BOTH Live dispatch and Itinerary mode; only the fixed shuttle route
 * types are excluded.
 *
 * This is deliberately NOT the same rule as on-demand offers (see
 * isOnDemandAvailable): marketplace is driver-initiated browsing that the server
 * validates per trip — it returns canClaim/canBid per result and enforces the fleet's
 * driverSelfAssembly/tripBidding config — whereas an on-demand offer is dispatch
 * pushing a live job at the driver, which only Live dispatch can answer.
 *
 * An unknown/missing type stays available: 'live' is the default mode, so absence must
 * not silently remove the tile.
 */
export function isMarketplaceAvailable(dispatchType) {
  const type = String(dispatchType || '').trim().toLowerCase();
  if (!type) return true; // absent = live, the default mode
  if (MARKETPLACE_DISPATCH_TYPES.includes(type)) return true;
  // Unknown types keep the previous permissive behaviour; only the fixed shuttle
  // sequences are actively excluded.
  return !FIXED_SEQUENCE_DISPATCH_TYPES.includes(type);
}

/**
 * On-demand job offers (JOBOM → ACPTM/REJTM) are Live dispatch only — "on-demand" and
 * "Live dispatch" are the same thing. An itinerary driver's sequence comes from the
 * route dispatch assigned them, and there is no offer UI in itinerary mode to answer
 * with, so an offer pushed there could only ever time out.
 *
 * Marketplace is separate and IS available in itinerary; see isMarketplaceAvailable.
 */
export function isOnDemandAvailable(dispatchType) {
  const type = String(dispatchType || '').trim().toLowerCase();
  if (!type) return true; // absent = live, the default mode
  return !FIXED_SEQUENCE_DISPATCH_TYPES.includes(type);
}

/**
 * How long an on-demand trip offer stays on screen before it auto-accepts, when the
 * fleet has not configured its own `driverTripRejection` window. The driver has to read
 * the pickup and destination and decide inside this window, so it is deliberately a
 * buffer rather than a reflex test. 10s was too short and auto-accepted mid-read.
 */
export const ON_DEMAND_OFFER_BUFFER_SECONDS = 30;

export default isMarketplaceAvailable;
